import { useMemo, useState } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import {
    TransformationReferenceAdmin,
    TransformationReferenceWrite,
    useCreateTransformationReference,
    useDeleteTransformationReference,
    useTransformationReferencesAdmin,
    useUpdateTransformationReference,
} from "src/api/sure-api/useTransformationReferencesAdmin";
import { useSnackbar } from "src/utils/useSnackbar";
import { EntityListLayout } from "src/components/Grid";
import { GridPageHeader } from "src/components/Grid/PageGridHeader";
import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import {
    TransformationReferenceDeleteDialog,
    TransformationReferenceDialog,
    TransformationReferenceFormState,
} from "./TransformationReferenceDialogs";
import { TransformationReferencesLegacyView } from "./TransformationReferencesLegacyView";
import { TransformationReferencesTable } from "./TransformationReferencesTable";

const emptyForm: TransformationReferenceFormState = {
    code: "",
    name: "",
    ecobalyseProcessUuid: "",
    ssq: "",
    sourceVersion: "",
};

const relationToIri = (
    value: TransformationReferenceAdmin["ssq"] | TransformationReferenceAdmin["sourceVersion"],
    collectionIri: "/api/ssqs" | "/api/source_versions",
): string => {
    if (typeof value === "string") {
        return value;
    }

    return value?.["@id"] ?? (value?.id === undefined ? "" : `${collectionIri}/${value.id}`);
};

const toFormState = (reference: TransformationReferenceAdmin | null): TransformationReferenceFormState => {
    if (reference === null) {
        return emptyForm;
    }

    return {
        code: reference.code,
        name: reference.name,
        ecobalyseProcessUuid: reference.ecobalyseProcessUuid ?? "",
        ssq: relationToIri(reference.ssq, "/api/ssqs"),
        sourceVersion: relationToIri(reference.sourceVersion, "/api/source_versions"),
    };
};

const toPayload = (form: TransformationReferenceFormState): TransformationReferenceWrite => ({
    code: form.code.trim(),
    name: form.name.trim(),
    ecobalyseProcessUuid: form.ecobalyseProcessUuid.trim() === "" ? null : form.ecobalyseProcessUuid.trim(),
    ssq: form.ssq.trim() === "" ? null : form.ssq.trim(),
    sourceVersion: form.sourceVersion.trim() === "" ? null : form.sourceVersion.trim(),
});

export const TransformationReferencesPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const snackbar = useSnackbar();
    const { isFeatureActivated } = useApplicationParameters();
    const isProductUxV2 = useIsProductUxV2();
    const { transformationReferences, isLoading, isError, refetch } = useTransformationReferencesAdmin();
    const createReference = useCreateTransformationReference();
    const updateReference = useUpdateTransformationReference();
    const deleteReference = useDeleteTransformationReference();

    const [search, setSearch] = useState("");
    const [editingReference, setEditingReference] = useState<TransformationReferenceAdmin | null>(null);
    const [referencePendingDelete, setReferencePendingDelete] = useState<TransformationReferenceAdmin | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [form, setForm] = useState<TransformationReferenceFormState>(emptyForm);

    const pageTitle = t("transformation.references.title", "Manage the transformation processes");
    const pageSubtitle = t(
        "transformation.references.subtitle",
        "Configure the transformation references used for product Level 2 transformation scoring.",
    );

    const filteredReferences = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        if (normalizedSearch === "") {
            return transformationReferences;
        }

        return transformationReferences.filter((reference) => {
            return [reference.code, reference.name, reference.ecobalyseProcessUuid ?? ""].some((value) =>
                value.toLowerCase().includes(normalizedSearch),
            );
        });
    }, [search, transformationReferences]);

    const openCreateDialog = () => {
        setEditingReference(null);
        setForm(emptyForm);
        setIsDialogOpen(true);
    };

    const openEditDialog = (reference: TransformationReferenceAdmin) => {
        setEditingReference(reference);
        setForm(toFormState(reference));
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setEditingReference(null);
        setForm(emptyForm);
    };

    const handleSubmit = () => {
        const payload = toPayload(form);
        if (payload.code === "" || payload.name === "") {
            snackbar.setSnackbarProps({
                type: "error",
                message: t("transformation.references.validation.required", "Code and name are required."),
            });

            return;
        }

        const callbacks = {
            onSuccess: () => {
                snackbar.setSnackbarProps({
                    type: "success",
                    message:
                        editingReference === null ?
                            t("transformation.references.create_success", "Transformation process created.")
                        :   t("transformation.references.update_success", "Transformation process updated."),
                });
                closeDialog();
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t("transformation.references.save_error", "Unable to save the transformation process."),
                });
            },
        };

        if (editingReference === null) {
            createReference.mutate(payload, callbacks);
        } else {
            updateReference.mutate({ id: editingReference.id, payload }, callbacks);
        }
    };

    const handleDelete = () => {
        if (referencePendingDelete === null) {
            return;
        }

        deleteReference.mutate(referencePendingDelete.id, {
            onSuccess: () => {
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("transformation.references.delete_success", "Transformation process deleted."),
                });
                setReferencePendingDelete(null);
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t(
                        "transformation.references.delete_error",
                        "Unable to delete the transformation process.",
                    ),
                });
            },
        });
    };

    const handleLegacyDelete = (reference: TransformationReferenceAdmin) => {
        const confirmed = window.confirm(
            t(
                "transformation.references.delete_confirm",
                "Delete this transformation process? This action cannot be undone.",
            ),
        );
        if (!confirmed) {
            return;
        }

        deleteReference.mutate(reference.id, {
            onSuccess: () => {
                snackbar.setSnackbarProps({
                    type: "success",
                    message: t("transformation.references.delete_success", "Transformation process deleted."),
                });
            },
            onError: () => {
                snackbar.setSnackbarProps({
                    type: "error",
                    message: t(
                        "transformation.references.delete_error",
                        "Unable to delete the transformation process.",
                    ),
                });
            },
        });
    };

    if (!isFeatureActivated("FS-manage-my-transformation")) {
        return (
            <Box sx={{ padding: 4 }}>
                <RemoteErrorAlert message={t("common.accessDenied", "Access denied")} status="forbidden" />
            </Box>
        );
    }

    if (!isProductUxV2) {
        return (
            <TransformationReferencesLegacyView
                title={pageTitle}
                subtitle={pageSubtitle}
                search={search}
                references={filteredReferences}
                isLoading={isLoading}
                isError={isError}
                isDialogOpen={isDialogOpen}
                editingReference={editingReference}
                form={form}
                isSaving={createReference.isLoading || updateReference.isLoading}
                onSearchChange={setSearch}
                onCreate={openCreateDialog}
                onEdit={openEditDialog}
                onDelete={handleLegacyDelete}
                onRetry={() => void refetch()}
                onCloseDialog={closeDialog}
                onFormChange={setForm}
                onSubmit={handleSubmit}
            />
        );
    }

    return (
        <>
            <EntityListLayout
                appearance="hub"
                ariaLabel={pageTitle}
                heading={
                    <Stack gap={theme.spacing(1)}>
                        <GridPageHeader title={pageTitle} />
                        <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                            {pageSubtitle}
                        </Typography>
                    </Stack>
                }
                search={{
                    onChange: setSearch,
                    placeholder: t("transformation.references.search", "Search processes..."),
                    showOnDesktop: true,
                    value: search,
                }}
                actions={
                    <Button variant="primary" startIcon={<FoodsIcon icon="plus" size={2} />} onClick={openCreateDialog}>
                        {t("transformation.references.add", "Add process")}
                    </Button>
                }
                mobileActions={
                    <Button
                        variant="primary"
                        startIcon={<FoodsIcon icon="plus" size={2} />}
                        onClick={openCreateDialog}
                        fullWidth
                    >
                        {t("transformation.references.add", "Add process")}
                    </Button>
                }
            >
                <TransformationReferencesTable
                    references={filteredReferences}
                    isLoading={isLoading}
                    isError={isError}
                    canManage
                    onRetry={() => void refetch()}
                    onEdit={openEditDialog}
                    onDelete={setReferencePendingDelete}
                />
            </EntityListLayout>

            <TransformationReferenceDialog
                open={isDialogOpen}
                form={form}
                isSaving={createReference.isLoading || updateReference.isLoading}
                isEditing={editingReference !== null}
                onClose={closeDialog}
                onChange={setForm}
                onSubmit={handleSubmit}
            />

            <TransformationReferenceDeleteDialog
                reference={referencePendingDelete}
                isDeleting={deleteReference.isLoading}
                onClose={() => setReferencePendingDelete(null)}
                onConfirm={handleDelete}
            />
        </>
    );
};
